import { useMemo } from 'react';
import useGameStore, { BANK_ID, MONEY_TRANSFER_TTL_MS } from '../store/gameStore';
import { money } from '../lib/format';
import type { CSSProperties } from 'react';

/** Where a chip starts / lands: a player card, the bank anchor, or the viewport centre-top. */
const anchorOf = (id: string, overlay?: HTMLElement | null) => {
  if (id === BANK_ID) {
    const bank = document.querySelector('[data-bank-anchor]');
    if (bank) {
      const r = bank.getBoundingClientRect();
      return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
    }
    return { x: window.innerWidth / 2, y: 72 };
  }
  const card = document.querySelector(`[data-player-card="${id}"]`);
  if (card) {
    const r = card.getBoundingClientRect();
    return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
  }
  const rect = overlay?.getBoundingClientRect();
  return { x: (rect?.left ?? 0) + (rect?.width ?? window.innerWidth) / 2, y: (rect?.top ?? 0) + (rect?.height ?? window.innerHeight) / 2 };
};

/** Measured once per transfer id, so a re-render never re-anchors a chip mid-flight. */
const anchorCache = new Map<number, { start: { x: number; y: number }; end: { x: number; y: number } }>();

/**
 * Slow flying-cash chips: every store money-transfer event renders one chip that travels
 * from the payer's ledger card to the recipient's card (or to/from the bank anchor) over
 * ~1.35 s along a floating arc, with a trailing +/- amount label.
 */
export const MoneyTransferOverlay = () => {
  const transfers = useGameStore((s) => s.moneyTransfers);
  const players = useGameStore((s) => s.players);
  // Only chips that started just now are animated; older ones have already landed.
  const chips = useMemo(
    () => transfers.filter((t) => Date.now() - t.ts < MONEY_TRANSFER_TTL_MS - 150),
    [transfers],
  );
  if (chips.length === 0) return null;
  return (
    <div className="pointer-events-none fixed inset-0 z-[68]">
      {chips.map((transfer) => {
        let anchors = anchorCache.get(transfer.id);
        if (!anchors) {
          anchors = { start: anchorOf(transfer.fromId, null), end: anchorOf(transfer.toId, null) };
          anchorCache.set(transfer.id, anchors);
          if (anchorCache.size > 60) anchorCache.delete([...anchorCache.keys()][0]);
        }
        const { start, end } = anchors;
        const payer = players.find((p) => p.id === transfer.fromId);
        const receiver = players.find((p) => p.id === transfer.toId);
        const tone = receiver ? 'text-emerald-200 border-emerald-300/70 bg-emerald-500/25' : 'text-rose-200 border-rose-300/70 bg-rose-500/25';
        const style = {
          left: `${start.x}px`,
          top: `${start.y}px`,
          '--dx': `${end.x - start.x}px`,
          '--dy': `${end.y - start.y}px`,
          '--dur': '1350ms',
        } as CSSProperties;
        return (
          <span key={transfer.id} className={`money-chip flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-black backdrop-blur ${tone}`} style={style}>
            {transfer.fromId === BANK_ID ? '' : (payer?.name.charAt(0) ?? '?')} → {transfer.toId === BANK_ID ? '' : (receiver?.name.charAt(0) ?? '?')} {money(transfer.amount)}
          </span>
        );
      })}
    </div>
  );
};

export default MoneyTransferOverlay;
